import React, { useEffect, useState } from 'react';
import axios from 'axios';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const fmtGBP = (pence, currency = 'gbp') => {
  if (pence == null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() })
      .format((pence || 0) / 100);
  } catch {
    return `£${((pence || 0) / 100).toFixed(2)}`;
  }
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const STATUS_BADGE = {
  active:    { label: 'Active',     tone: 'good' },
  trialing:  { label: 'Trial',      tone: 'good' },
  past_due:  { label: 'Past due',   tone: 'warn' },
  unpaid:    { label: 'Unpaid',     tone: 'warn' },
  canceled:  { label: 'Cancelled',  tone: 'muted' },
  incomplete:        { label: 'Incomplete', tone: 'warn' },
  incomplete_expired:{ label: 'Expired',    tone: 'muted' },
};

function StatCard({ label, value, sub, accent = 'amber', icon }) {
  return (
    <div className="admin-card" style={{ display: 'flex', alignItems: 'center', gap: 14, margin: 0, padding: '18px 20px' }}>
      <div style={{
        width: 46, height: 46, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 12, background: 'linear-gradient(135deg, var(--a1), var(--a2))',
        color: '#1a1207', fontSize: 22, flexShrink: 0,
        ...(accent === 'amber'    && { '--a1': '#b8860b', '--a2': '#daa520' }),
        ...(accent === 'sapphire' && { '--a1': '#4a7fd1', '--a2': '#7da4eb' }),
        ...(accent === 'rose'     && { '--a1': '#c75d3a', '--a2': '#e76f51' }),
        ...(accent === 'mint'     && { '--a1': '#48a88e', '--a2': '#78d2b4' }),
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', color: 'var(--admin-fg-strong)', lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: '0.72rem', letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--admin-fg-faint)' }}>{label}</div>
        {sub && <div style={{ fontSize: '0.78rem', color: 'var(--admin-fg-faint)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

const TONE_COLORS = {
  good:  { bg: 'rgba(72,168,142,0.14)',  fg: '#7ed3a8',                 border: 'rgba(72,168,142,0.4)' },
  warn:  { bg: 'rgba(220,170,60,0.16)',  fg: '#ffd896',                 border: 'rgba(220,170,60,0.4)' },
  bad:   { bg: 'rgba(220,90,90,0.18)',   fg: '#ff9b9b',                 border: 'rgba(220,90,90,0.45)' },
  muted: { bg: 'rgba(255,255,255,0.05)', fg: 'rgba(245,230,200,0.55)',  border: 'rgba(255,255,255,0.08)' },
};

function Badge({ label, tone = 'muted' }) {
  const c = TONE_COLORS[tone] || TONE_COLORS.muted;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
    }}>{label}</span>
  );
}

function StatusBadge({ status, cancelling }) {
  const cfg = STATUS_BADGE[status] || { label: status || '—', tone: 'muted' };
  const label = (cancelling && (status === 'active' || status === 'trialing')) ? 'Cancelling' : cfg.label;
  const tone  = (cancelling && (status === 'active' || status === 'trialing')) ? 'warn' : cfg.tone;
  return <Badge label={label} tone={tone} />;
}

function ChargeBadge({ status, refunded, partialRefund }) {
  if (status === 'failed')  return <Badge label="Failed"   tone="bad" />;
  if (status === 'pending') return <Badge label="Pending"  tone="warn" />;
  if (refunded)             return <Badge label="Refunded" tone="muted" />;
  if (partialRefund)        return <Badge label="Partial refund" tone="warn" />;
  if (status === 'succeeded') return <Badge label="Paid"   tone="good" />;
  return <Badge label={status || '—'} tone="muted" />;
}

export default function AdminBilling() {
  const [summary, setSummary] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [subFilter, setSubFilter] = useState('all'); // 'all' | 'parent' | 'school'
  const [transactions, setTransactions] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    setErr('');
    return Promise.all([
      axios.get('/api/admin/billing/summary',      { headers: authHeaders() }),
      axios.get('/api/admin/billing/subscribers',  { headers: authHeaders() }),
      axios.get('/api/admin/billing/transactions', { headers: authHeaders() }),
    ])
      .then(([s, subs, tx]) => {
        setSummary(s.data);
        setSubscribers(subs.data || []);
        setTransactions(tx.data || []);
      })
      .catch((e) => setErr(e?.response?.data?.message || 'Failed to load billing data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const counts = summary?.counts || {};
  const revenue = summary?.revenue || {};
  const conversionPct = summary ? (summary.conversion * 100).toFixed(1) : '—';

  return (
    <div className="admin-page">
      <header className="admin-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="admin-page-title">Revenue &amp; Billing</h1>
          <p className="admin-page-sub">Subscriber metrics and Stripe activity.</p>
        </div>
        <button type="button" className="admin-btn admin-btn-ghost" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </header>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 22 }}>
        <StatCard label="Monthly recurring revenue" value={fmtGBP(summary?.mrr)} sub={`${counts.payingNow ?? 0} paying now`} accent="amber"    icon="💷" />
        <StatCard label="Last 30 days revenue"      value={fmtGBP(revenue.thirtyDays)} accent="sapphire" icon="📈" />
        <StatCard label="This month revenue"        value={fmtGBP(revenue.thisMonth)}  accent="mint"     icon="🗓️" />
        <StatCard label="All-time revenue"          value={fmtGBP(revenue.allTime)} sub={revenue.refundedAllTime ? `Refunded ${fmtGBP(revenue.refundedAllTime)}` : null} accent="amber" icon="🏦" />
        <StatCard label="Active subscribers"        value={counts.active ?? '—'} sub={counts.cancelling ? `${counts.cancelling} cancelling` : null} accent="mint" icon="👤" />
        <StatCard label="Past due"                  value={counts.past_due ?? '—'} accent="rose"     icon="⚠️" />
        <StatCard
          label="Cancellations"
          value={(counts.cancelling || 0) + (counts.canceled || 0)}
          sub={
            (counts.cancelling || counts.canceled)
              ? `${counts.cancelling || 0} pending · ${counts.canceled || 0} ended`
              : null
          }
          accent="sapphire"
          icon="🚪"
        />
        <StatCard label="Conversion rate"           value={`${conversionPct}%`} sub={`of ${counts.verifiedUsers ?? 0} verified users`} accent="amber" icon="📊" />
      </div>

      {(() => {
        const counts = {
          all: subscribers.length,
          parent: subscribers.filter((s) => s.kind === 'parent').length,
          school: subscribers.filter((s) => s.kind === 'school').length,
        };
        const visible = subFilter === 'all' ? subscribers : subscribers.filter((s) => s.kind === subFilter);
        return (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--admin-fg-strong)', fontSize: '1.3rem', margin: 0 }}>
                Subscribers <span style={{ fontSize: '0.85rem', color: 'var(--admin-fg-faint)' }}>({visible.length})</span>
              </h2>
              <div style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 999, background: 'rgba(13,10,5,0.4)', border: '1px solid rgba(184,134,11,0.25)' }}>
                {[
                  { id: 'all',    label: `All (${counts.all})` },
                  { id: 'parent', label: `Parents (${counts.parent})` },
                  { id: 'school', label: `Schools (${counts.school})` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSubFilter(tab.id)}
                    style={{
                      padding: '6px 14px', borderRadius: 999, border: 0, cursor: 'pointer',
                      fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.82rem', fontWeight: 600,
                      letterSpacing: '0.3px',
                      background: subFilter === tab.id ? 'linear-gradient(180deg,#f0c560,#b8860b)' : 'transparent',
                      color: subFilter === tab.id ? '#1a140a' : 'rgba(245,230,200,0.7)',
                      transition: 'background 180ms ease, color 180ms ease',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="admin-card" style={{ padding: 0, overflowX: 'auto', marginBottom: 22 }}>
              <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
                <thead>
                  <tr>
                    <th style={th}>Type</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Plan</th>
                    <th style={th}>Status</th>
                    <th style={th}>Period end</th>
                    <th style={th}>Stripe customer</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr><td colSpan="6" style={{ padding: 22, textAlign: 'center', color: 'var(--admin-fg-faint)' }}>No subscribers yet.</td></tr>
                  )}
                  {visible.map((s) => (
                    <tr key={`${s.kind}-${s._id}`} style={tr}>
                      <td style={td}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                          fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                          background: s.kind === 'school' ? 'rgba(184,134,11,0.18)' : 'rgba(91,139,58,0.18)',
                          color:      s.kind === 'school' ? '#f0c560' : '#9fe39d',
                          border:     s.kind === 'school' ? '1px solid rgba(240,197,96,0.4)' : '1px solid rgba(91,139,58,0.5)',
                        }}>
                          {s.kind === 'school' ? '🏫 School' : '👤 Parent'}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={{ color: 'var(--admin-fg-strong)', fontWeight: 600 }}>{s.name || s.email}</div>
                        {s.name && s.email && <div style={{ fontSize: '0.78rem', color: 'var(--admin-fg-faint)' }}>{s.email}</div>}
                        {s.kind === 'school' && (
                          <div style={{ fontSize: '0.74rem', color: 'var(--admin-fg-faint)', marginTop: 4, display: 'grid', gap: 2 }}>
                            {s.contactName && <div>Contact: <span style={{ color: 'var(--admin-fg)' }}>{s.contactName}</span></div>}
                            {s.country && <div>{s.country}</div>}
                            <div>
                              <span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>code: {s.schoolCode}</span>
                              <span> · {s.studentCount || 0} student{s.studentCount === 1 ? '' : 's'}</span>
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {s.kind === 'school' ? (
                          s.tier
                            ? <div style={{ color: 'var(--admin-fg-strong)' }}>{s.tier}<span style={{ fontSize: '0.78rem', color: 'var(--admin-fg-faint)' }}> · {s.billingCycle || 'monthly'}</span></div>
                            : <span style={{ color: 'var(--admin-fg-faint)' }}>—</span>
                        ) : (
                          <span style={{ color: 'var(--admin-fg-faint)' }}>{s.priceId || '—'}</span>
                        )}
                      </td>
                      <td style={td}><StatusBadge status={s.status} cancelling={s.cancelAtPeriodEnd} /></td>
                      <td style={td}>{fmtDate(s.currentPeriodEnd)}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--admin-fg-faint)' }}>{s.stripeCustomerId || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      <h2 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--admin-fg-strong)', fontSize: '1.3rem', marginBottom: 12 }}>
        Recent transactions <span style={{ fontSize: '0.85rem', color: 'var(--admin-fg-faint)' }}>(last {transactions.length})</span>
      </h2>
      <div className="admin-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              <th style={th}>Date</th>
              <th style={th}>Customer</th>
              <th style={th}>Amount</th>
              <th style={th}>Card</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 && (
              <tr><td colSpan="6" style={{ padding: 22, textAlign: 'center', color: 'var(--admin-fg-faint)' }}>No transactions yet.</td></tr>
            )}
            {transactions.map((t) => (
              <tr key={t.id} style={tr}>
                <td style={td}>{fmtDateTime(t.created)}</td>
                <td style={td}>{t.email || <span style={{ color: 'var(--admin-fg-faint)' }}>—</span>}</td>
                <td style={td}>
                  {fmtGBP(t.amount, t.currency)}
                  {t.amountRefunded > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#ffb39a' }}>
                      Refunded {fmtGBP(t.amountRefunded, t.currency)}
                    </div>
                  )}
                </td>
                <td style={td}>{t.cardBrand && t.cardLast4 ? `${t.cardBrand.toUpperCase()} •••• ${t.cardLast4}` : '—'}</td>
                <td style={td}>
                  <ChargeBadge
                    status={t.status}
                    refunded={t.refunded}
                    partialRefund={!t.refunded && t.amountRefunded > 0}
                  />
                </td>
                <td style={td}>
                  {t.receiptUrl && (
                    <a href={t.receiptUrl} target="_blank" rel="noreferrer" className="admin-btn admin-btn-ghost" style={{ padding: '4px 10px', fontSize: '0.78rem' }}>
                      Receipt ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {summary?.generatedAt && (
        <div style={{ marginTop: 14, fontSize: '0.76rem', color: 'var(--admin-fg-faint)', textAlign: 'right' }}>
          Updated {fmtDateTime(summary.generatedAt)}
        </div>
      )}
    </div>
  );
}

const th = { textAlign: 'left', padding: '12px 16px', fontSize: '0.72rem', letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--admin-fg-faint)', borderBottom: '1px solid rgba(255,255,255,0.06)' };
const td = { padding: '12px 16px', verticalAlign: 'top', color: 'var(--admin-fg)' };
const tr = { borderBottom: '1px solid rgba(255,255,255,0.04)' };
